import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * useTextSelection Hook
 * 
 * Detects text selection within transcript blocks and validates whole word selection
 */
const useTextSelection = (containerRef) => {
  const [selection, setSelection] = useState(null);
  const selectionTimeoutRef = useRef(null);

  /**
   * Get selection position (kept for potential future use)
   */
  const getSelectionPosition = useCallback((range) => {
    const rects = range.getBoundingClientRect();
    const containerRect = containerRef?.current?.getBoundingClientRect() || { left: 0, top: 0 };

    return {
      top: rects.top - containerRect.top - 60, // Position toolbar above selection
      left: rects.left - containerRect.left + (rects.width / 2), // Center horizontally
      width: rects.width,
    };
  }, [containerRef]);

  /**
   * Handle selection change
   */
  const handleSelectionChange = useCallback(() => {
    // Clear existing timeout
    if (selectionTimeoutRef.current) {
      clearTimeout(selectionTimeoutRef.current);
    }

    // Debounce selection detection
    selectionTimeoutRef.current = setTimeout(() => {
      const windowSelection = window.getSelection();
      
      if (!windowSelection || windowSelection.rangeCount === 0) {
        setSelection(null);
        return;
      }

      const range = windowSelection.getRangeAt(0);
      const selectedText = windowSelection.toString().trim();

      // Ignore empty selections or collapsed selections (just cursor)
      if (!selectedText || range.collapsed) {
        setSelection(null);
        return;
      }

      // Check if selection is within our container
      if (containerRef?.current && !containerRef.current.contains(range.commonAncestorContainer)) {
        setSelection(null);
        return;
      }

      // Set selection (no validation needed - toolbar removed)
      setSelection({
        text: selectedText,
        isValid: true,
        position: getSelectionPosition(range),
        range: range,
      });
    }, 100); // 100ms debounce
  }, [containerRef, getSelectionPosition]);

  /**
   * Clear selection
   */
  const clearSelection = useCallback(() => {
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, []);

  /**
   * Setup event listeners
   */
  useEffect(() => {
    document.addEventListener('selectionchange', handleSelectionChange);
    document.addEventListener('mouseup', handleSelectionChange);

    return () => {
      document.removeEventListener('selectionchange', handleSelectionChange);
      document.removeEventListener('mouseup', handleSelectionChange);
      
      if (selectionTimeoutRef.current) {
        clearTimeout(selectionTimeoutRef.current);
      }
    };
  }, [handleSelectionChange]);

  return {
    selection,
    clearSelection,
  };
};

export default useTextSelection;

